import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes with proper precedence.
 * Uses clsx for conditional classes and tailwind-merge to handle conflicts.
 *
 * @example
 * cn('px-2 py-1', condition && 'bg-red-500', 'px-4')
 * // Returns: 'py-1 px-4' (px-4 overrides px-2) plus conditional bg-red-500
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date for display
 */
export function formatDate(date: Date | string, locale = 'en-US'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format hours for display (e.g., 8.5 -> "8h 30m")
 */
export function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Get entity color class name
 */
export type EntityType = 'timesheet' | 'order' | 'client' | 'supplier' | 'user' | 'report';

export function getEntityColorClasses(entity: EntityType) {
  return {
    bg: `bg-entity-${entity}`,
    text: `text-entity-${entity}`,
    border: `border-entity-${entity}`,
  };
}

/**
 * Capitalize first letter
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
